import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Services() {
  useEffect(() => {
    // Add Schema.org JSON-LD for SEO
    const siteUrl = import.meta.env.VITE_SITE_URL || '';
    
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "ホーム",
          "item": siteUrl
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "事業内容",
          "item": `${siteUrl}/services`
        }
      ]
    };

    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "行政書士サービス",
      "provider": {
        "@type": "ProfessionalService",
        "name": "おかやま行政書士事務所",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "岡山市北区",
          "addressRegion": "岡山県",
          "addressCountry": "JP"
        }
      },
      "areaServed": {
        "@type": "City",
        "name": "岡山市"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "行政書士サービス",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "建設業許可申請",
              "description": "建設業を営むために必要な許可の取得をサポート"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "飲食店営業許可",
              "description": "飲食店、喫茶店、バーなどの営業許可申請を代行"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "会社設立・法人手続き",
              "description": "株式会社、合同会社の設立手続きを支援"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "相続・遺言手続き",
              "description": "相続手続き、遺言書作成、遺産分割協議書の作成"
            }
          }
        ]
      }
    };

    const script1 = document.createElement('script');
    script1.type = 'application/ld+json';
    script1.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.type = 'application/ld+json';
    script2.text = JSON.stringify(serviceSchema);
    document.head.appendChild(script2);

    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);

  const services = [
    {
      icon: 'ri-building-line',
      title: '建設業許可申請',
      description: '建設業を営むために必要な許可の取得をサポートします。新規申請から更新、業種追加、経営事項審査まで、建設業に関する許認可手続きを総合的にサポートいたします。',
      image: 'https://public.readdy.ai/ai/img_res/9d6b41a69cec096d5bf42de4e856168e.jpg',
      details: [
        '一般建設業許可・特定建設業許可の新規申請',
        '建設業許可の更新手続き（5年ごと）',
        '業種追加・般特新規申請',
        '経営事項審査（経審）の申請',
        '決算変更届・各種変更届の提出',
        '建設業許可の要件診断・事前相談'
      ],
      benefits: [
        '岡山県内の建設業者様の実績多数',
        '複雑な要件を分かりやすく説明',
        '書類作成から提出まで一貫サポート',
        '許可取得後のアフターフォローも充実'
      ]
    },
    {
      icon: 'ri-store-line',
      title: '飲食店営業許可',
      description: '飲食店、喫茶店、バーなどの営業許可申請を代行します。保健所との事前相談から許可取得まで、開業に必要な手続きをトータルサポートいたします。',
      image: 'https://readdy.ai/api/search-image?query=Modern%20stylish%20Japanese%20restaurant%20interior%20in%20Okayama%2C%20elegant%20dining%20space%20with%20wooden%20furniture%20and%20warm%20lighting%2C%20clean%20professional%20kitchen%20visible%2C%20welcoming%20atmosphere%2C%20high%20quality%20professional%20photography%20style&width=800&height=500&seq=service-detail2&orientation=landscape',
      details: [
        '飲食店営業許可申請',
        '喫茶店営業許可申請',
        '菓子製造業許可申請',
        '深夜酒類提供飲食店営業開始届',
        '食品衛生責任者の設置サポート',
        '保健所との事前相談・図面作成'
      ],
      benefits: [
        '保健所との調整もお任せ',
        '図面作成から許可取得まで対応',
        '開業スケジュールに合わせた迅速対応',
        '岡山市内の飲食店開業実績豊富'
      ]
    },
    {
      icon: 'ri-file-text-line',
      title: '会社設立・法人手続き',
      description: '株式会社、合同会社の設立手続きを支援します。定款作成から登記申請まで、起業・創業に必要な手続きを一貫してサポートいたします。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20business%20professionals%20celebrating%20successful%20company%20establishment%20in%20modern%20office%2C%20handshake%20and%20smiles%2C%20professional%20attire%2C%20bright%20natural%20lighting%2C%20clean%20corporate%20environment%2C%20success%20and%20achievement%20atmosphere%2C%20high%20quality%20professional%20photography&width=800&height=500&seq=service-detail3&orientation=landscape',
      details: [
        '株式会社・合同会社の設立',
        '定款の作成・認証手続き',
        '電子定款による印紙代節約',
        '設立登記申請（司法書士と連携）',
        '法人設立後の各種届出',
        '事業計画書作成サポート'
      ],
      benefits: [
        '設立費用を抑えた電子定款対応',
        '司法書士・税理士との連携でワンストップ',
        '創業融資の相談も可能',
        '岡山市での起業を全面サポート'
      ]
    },
    {
      icon: 'ri-parent-line',
      title: '相続・遺言手続き',
      description: '相続手続き、遺言書作成、遺産分割協議書の作成など、相続に関する手続きを丁寧にサポートします。ご家族の大切な財産を守るお手伝いをいたします。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20family%20consulting%20with%20professional%20advisor%20about%20inheritance%20matters%20in%20comfortable%20office%2C%20warm%20reassuring%20atmosphere%2C%20natural%20lighting%2C%20discussing%20important%20documents%20together%2C%20trust%20and%20care%20environment%2C%20high%20quality%20professional%20photography&width=800&height=500&seq=service-detail4&orientation=landscape',
      details: [
        '遺言書作成サポート（公正証書遺言・自筆証書遺言）',
        '遺産分割協議書の作成',
        '相続人調査・相続関係説明図作成',
        '金融機関の相続手続き代行',
        '不動産の名義変更（司法書士と連携）',
        '相続税申告（税理士と連携）'
      ],
      benefits: [
        '初回相談で丁寧にヒアリング',
        '複雑な相続関係も分かりやすく整理',
        '専門家ネットワークで総合サポート',
        '岡山市を中心に相続手続き実績多数'
      ]
    },
    {
      icon: 'ri-car-line',
      title: '自動車登録・車庫証明',
      description: '自動車の名義変更、車庫証明の取得など、自動車に関する手続きを迅速に代行します。岡山運輸支局での手続きもお任せください。',
      image: 'https://readdy.ai/api/search-image?query=Modern%20Japanese%20car%20dealership%20showroom%20in%20Okayama%20with%20new%20vehicles%20on%20display%2C%20clean%20professional%20environment%2C%20bright%20natural%20lighting%2C%20organized%20professional%20space%2C%20customer%20service%20area%20visible%2C%20high%20quality%20professional%20photography&width=800&height=500&seq=service-detail5&orientation=landscape',
      details: [
        '普通自動車・軽自動車の名義変更',
        '車庫証明（自動車保管場所証明）の取得',
        '自動車の新規登録・廃車手続き',
        '住所変更・氏名変更の登録',
        'ナンバープレートの変更',
        '出張封印サービス'
      ],
      benefits: [
        '岡山運輸支局での手続きに精通',
        '最短即日での車庫証明取得',
        '平日お忙しい方の代わりに手続き',
        '自動車販売店様との取引実績多数'
      ]
    },
    {
      icon: 'ri-passport-line',
      title: '在留資格・帰化申請',
      description: '外国人の方の在留資格申請、帰化申請、永住許可申請など、入管手続きをサポートします。岡山で働く外国人の方を支援します。',
      image: 'https://readdy.ai/api/search-image?query=Diverse%20international%20business%20team%20working%20together%20in%20modern%20Japanese%20office%20in%20Okayama%2C%20multicultural%20professional%20environment%2C%20collaborative%20atmosphere%2C%20natural%20lighting%2C%20inclusive%20workplace%2C%20professional%20business%20photography&width=800&height=500&seq=service-detail6&orientation=landscape',
      details: [
        '在留資格認定証明書交付申請',
        '在留資格変更許可申請',
        '在留期間更新許可申請',
        '永住許可申請',
        '帰化許可申請',
        '就労資格証明書交付申請'
      ],
      benefits: [
        '多言語対応可能（英語・中国語）',
        '入管法改正にも迅速対応',
        '企業の外国人雇用もサポート',
        '岡山入国管理局での手続き実績豊富'
      ]
    },
    {
      icon: 'ri-shield-check-line',
      title: '古物商許可申請',
      description: '中古品の売買を行うために必要な古物商許可の取得をサポートします。リサイクルショップ、中古車販売、ネットオークションなどの開業を支援します。',
      image: 'https://readdy.ai/api/search-image?query=Modern%20Japanese%20second-hand%20shop%20interior%20in%20Okayama%2C%20clean%20organized%20retail%20space%20with%20vintage%20items%20displayed%2C%20professional%20retail%20environment%2C%20warm%20lighting%2C%20welcoming%20atmosphere%2C%20high%20quality%20professional%20photography&width=800&height=500&seq=service-detail7&orientation=landscape',
      details: [
        '古物商許可の新規申請',
        '営業所の追加・変更届',
        '古物商許可証の書換え',
        '古物商許可の廃止届',
        'ホームページ使用の届出',
        '古物台帳の作成指導'
      ],
      benefits: [
        'ネットショップ開業もサポート',
        '警察署との調整もお任せ',
        '許可取得後の運営アドバイスも',
        '岡山県内全域対応'
      ]
    },
    {
      icon: 'ri-file-list-line',
      title: '契約書・内容証明作成',
      description: '各種契約書の作成、内容証明郵便の作成など、権利義務に関する書類作成をサポートします。トラブル予防から解決まで支援いたします。',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20business%20professional%20reviewing%20legal%20documents%20and%20contracts%20at%20modern%20office%20desk%2C%20professional%20attire%2C%20focused%20concentration%2C%20natural%20lighting%2C%20organized%20workspace%20with%20laptop%20and%20papers%2C%20high%20quality%20professional%20photography&width=800&height=500&seq=service-detail8&orientation=landscape',
      details: [
        '各種契約書の作成・リーガルチェック',
        '内容証明郵便の作成・発送',
        '示談書・合意書の作成',
        '念書・覚書の作成',
        '公正証書作成サポート',
        '契約書の英訳・和訳'
      ],
      benefits: [
        '法的に有効な書類を作成',
        'トラブル予防のアドバイス',
        '公証役場との調整も対応',
        '迅速な対応で問題解決をサポート'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[500px] sm:h-[600px] lg:h-[500px] bg-gradient-to-r from-blue-900 to-blue-700 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]"></div>
        </div>
        <div className="relative h-full flex items-center">
          <div className="w-full max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 sm:mb-6">事業内容</h1>
              <p className="text-base sm:text-lg lg:text-xl text-white/90 leading-relaxed">
                岡山市を中心に、建設業許可から相続手続きまで、幅広い行政手続きをサポートいたします。お客様の事業と生活を支える、信頼のパートナーとして。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12 sm:space-y-16 lg:space-y-20">
            {services.map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl overflow-hidden shadow-xl">
                <div className="grid lg:grid-cols-2 gap-0">
                  <div className={`h-full ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-top min-h-[300px] sm:min-h-[400px]"
                    />
                  </div>
                  <div className="p-6 sm:p-8 lg:p-10 xl:p-12 flex flex-col justify-center">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-blue-600 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                      <i className={`${service.icon} text-2xl sm:text-3xl text-white`}></i>
                    </div>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
                      {service.title}
                    </h2>
                    <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 leading-relaxed">
                      {service.description}
                    </p>

                    <div className="mb-6 sm:mb-8">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">対応業務</h3>
                      <ul className="space-y-2 sm:space-y-3">
                        {service.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start gap-2 sm:gap-3">
                            <i className="ri-check-line text-blue-600 text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                            <span className="text-sm sm:text-base text-gray-700">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-white rounded-xl p-4 sm:p-6">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">当事務所の強み</h3>
                      <ul className="space-y-2">
                        {service.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-start gap-2 sm:gap-3">
                            <i className="ri-star-fill text-yellow-400 text-base sm:text-lg mt-0.5 flex-shrink-0"></i>
                            <span className="text-xs sm:text-sm text-gray-700">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6">
            お気軽にご相談ください
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-white/90 mb-8 sm:mb-10">
            初回相談は無料です。岡山市の行政書士が、お客様の課題解決をサポートいたします。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:0860123456"
              className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-white text-blue-600 text-base sm:text-lg font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill mr-2 sm:mr-3 text-xl sm:text-2xl"></i>
              086-012-3456
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-blue-700 text-white text-base sm:text-lg font-semibold rounded-lg hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill mr-2 sm:mr-3 text-xl sm:text-2xl"></i>
              お問い合わせフォーム
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}